import { useState } from 'react';

import { Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';
import { Plus } from '@keboola/design/icons';

import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { useInviteDeveloper } from '../queries';
import { inviteDeveloperFormSchema } from '../schemas';

const FORM_ID = 'invite-developer';

/**
 * Emails an invitation to join the current vendor.
 *
 * No vendor picker: the original renders one only when it has no vendor in context, which is the admin
 * panel's case. Here the vendor is always the one the dashboard is scoped to, and offering a choice would
 * let a developer invite someone into a vendor they cannot see.
 */
export function InviteDeveloperModal({ vendorId }: { vendorId: string }) {
  const [open, setOpen] = useState(false);
  const invite = useInviteDeveloper(vendorId);

  const form = useAppForm({
    defaultValues: { email: '' },
    validators: formValidators(inviteDeveloperFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      // Trimmed on the way out, matching the original's `normalizeEmail`: a copy-pasted address routinely
      // carries a trailing space, and apps-api treats it as part of the address.
      const email = value.email.trim();
      try {
        await invite.mutateAsync(email);
        toast.success(`An invitation was sent to ${email}.`);
        setOpen(false);
        form.reset();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not send the invitation.'));
      }
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.reset();
      }}
    >
      <Modal.Trigger asChild>
        <Button variant="primary">
          <Plus />
          Invite a developer
        </Button>
      </Modal.Trigger>
      <Modal.Content size="medium">
        <Modal.Header
          description={`They will be able to manage every component belonging to ${vendorId}.`}
        >
          Invite a developer
        </Modal.Header>
        <Modal.Body>
          <form.AppForm>
            <form.FormBody id={FORM_ID}>
              <form.AppField name="email">
                {(field) => (
                  <field.TextField
                    label="Email address"
                    type="email"
                    required
                    placeholder="developer@example.com"
                    help="We email them a link to accept the invitation."
                  />
                )}
              </form.AppField>
              <form.SubmitErrorAlert />
            </form.FormBody>
          </form.AppForm>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Modal.Close>
          <form.AppForm>
            <form.SubmitButton form={FORM_ID}>Send invitation</form.SubmitButton>
          </form.AppForm>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
